import React, { PureComponent } from 'react'
import MediaComponent from './mediaComponent'
import isEqual from 'lodash-es/isEqual'
import OpacityHelper from 'aria-viewer/tour/components/opacityHelper'
import LoadingIndicator from 'aria-viewer/loadingscreen/container/LoadingIndicator'
import styleguide from 'aria-viewer/tour/constants/styleGuide'
import Browser from 'aria-viewer/utils/mobileDetect'
import idx from 'idx'
import { MEDIA_TYPES } from 'aria-viewer/tour/constants'
import { imgLoaded } from 'aria-viewer/utils/functions'

const AbsoluteStyle = { position: 'absolute' }

const STOPPED = 0
const STARTED = 1

const S = {
    loadingIndicatorContainer: {
        zIndex: 3,
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%,-50%)',
        width: 200,
        height: 200,
        display: 'block',
    },
}

/**
 *	This class handles transitions between instances of MediaComponent
 *
 **/
class MediaComponentManager extends PureComponent {
    constructor(props) {
        super(props)

        this.state = {
            options: [null, null],
            secondaryOptions: [null, null],
            active: 1,
            targetActive: 0,
            loading: STOPPED,
        }

        this.mediaRefs = [React.createRef(), React.createRef()]

        this.onLoad = this.onLoad.bind(this)
        this.renderFirstChild = this.renderFirstChild.bind(this)
        this.renderSecondChild = this.renderSecondChild.bind(this)
        this.firstChildUnmount = this.onMediaComponentUnmount.bind(this, 0)
        this.secondChildUnmount = this.onMediaComponentUnmount.bind(this, 1)
    }

    init(props) {
        if (this.props === props || this.props.style !== props.style) {
            this.ContainerStyle = Object.assign({ position: 'relative', pointerEvents: 'none' }, props.style)
        }

        // don't refresh on width/height change
        const newMediaPath = idx(props.options, _ => (_.path ? _.path : _.url))
        const prevMediaPath = idx(this.state.options[this.state.active], _ => (_.path ? _.path : _.url))

        if (newMediaPath == null || !isEqual(newMediaPath, prevMediaPath)) {
            const options = this.state.options.slice()
            const secondaryOptions = this.state.secondaryOptions.slice()
            const active = this.state.active ? 0 : 1

            secondaryOptions[active] = { ...props.secondaryOptions }

            //Going back to a state that was already loaded
            if (![options[1], options[0]].includes(null) && isEqual(options[active], props.options)) {
                return this.setState({
                    secondaryOptions,
                    active,
                    targetActive: active,
                    loading: STOPPED,
                })
            }

            options[active] = { ...props.options }

            this.setState({
                targetActive: active,
                options,
                secondaryOptions,
                loading: STARTED,
            })
        } else if (!isEqual(props.secondaryOptions, this.state.secondaryOptions[this.state.active])) {
            const secondaryOptions = this.state.secondaryOptions.slice()
            secondaryOptions[this.state.active] = { ...props.secondaryOptions }
            this.setState({
                secondaryOptions,
            })
        }
    }

    componentWillMount() {
        this.init(this.props)
    }

    componentWillReceiveProps(nextProps) {
        this.init(nextProps)
    }

    onLoad(e) {
        const { targetActive } = this.state
        const { onLoad } = this.props
        this.setState({
            active: targetActive,
            loading: STOPPED,
        })
        onLoad && onLoad(e, this.mediaRefs[targetActive].current)
    }

    onMediaComponentUnmount = active => {
        if (this.props.options.mediaType == MEDIA_TYPES.photo && [active, undefined].includes(this.lastUnmounted)) {
            const prevActive = active ? 0 : 1

            // Photos may not have unmounted thus not calling onload when served from cache.
            // Therefore we'll check if the image has loaded directly from the dom element.
            if (imgLoaded(this.mediaRefs[prevActive].current)) {
                const { onLoad } = this.props

                onLoad && onLoad(null, this.mediaRefs[prevActive].current)
            }
        }

        this.lastUnmounted = active
    }

    renderFirstChild() {
        const loadFunc = this.state.targetActive == 0 ? this.onLoad : this.props.onLoad
        const { options, secondaryOptions } = this.state

        return (
            <MediaComponent
                {...this.props}
                options={options[0]}
                secondaryOptions={secondaryOptions[0]}
                positionalmedia={this.props.positionalmedia}
                onLoad={loadFunc}
                active={this.state.targetActive == 0}
                ref={this.mediaRefs[0]}
            />
        )
    }

    renderSecondChild() {
        const loadFunc = this.state.targetActive == 1 ? this.onLoad : this.props.onLoad
        const { options, secondaryOptions } = this.state

        return (
            <MediaComponent
                {...this.props}
                options={options[1]}
                secondaryOptions={secondaryOptions[1]}
                positionalmedia={this.props.positionalmedia}
                onLoad={loadFunc}
                active={this.state.targetActive == 1}
                ref={this.mediaRefs[1]}
            />
        )
    }

    render() {
        const { active, loading, secondaryOptions } = this.state
        const { mainMediaTransition } = styleguide.delay
        const { onClick } = this.props

        return (
            <div id="scene_main_media" onClick={onClick}>
                <div style={this.ContainerStyle}>
                    <LoadingIndicator
                        style={S.loadingIndicatorContainer}
                        class="gray"
                        outerSizes={!Browser.tablet() ? { outerWidth: 24, outerHeight: 24 } : {}}
                    />
                    <OpacityHelper
                        onUnMounted={this.firstChildUnmount}
                        test={active == 0}
                        style={AbsoluteStyle}
                        ignoreUnmount={loading != STOPPED}
                        delay={secondaryOptions[0] ? secondaryOptions[0].delay : mainMediaTransition}
                    >
                        {this.renderFirstChild}
                    </OpacityHelper>
                    <OpacityHelper
                        onUnMounted={this.secondChildUnmount}
                        test={active == 1}
                        style={AbsoluteStyle}
                        ignoreUnmount={loading != STOPPED}
                        delay={secondaryOptions[1] ? secondaryOptions[1].delay : mainMediaTransition}
                    >
                        {this.renderSecondChild}
                    </OpacityHelper>
                </div>
            </div>
        )
    }
}

export default MediaComponentManager
